<template>
  <main class="oauth-page">
    <section class="oauth-card" aria-labelledby="oauth-title">
      <div class="brand"><connector-brand-icon brand="gstocky" :size="34" /><span>Gstocky</span></div>
      <template v-if="loading">
        <h1 id="oauth-title">Checking connection…</h1>
        <p>Please wait while Gstocky verifies this request.</p>
      </template>

      <template v-else-if="error">
        <h1 id="oauth-title">Connection unavailable</h1>
        <p class="error">{{ error }}</p>
        <button class="secondary" type="button" @click="$router.push('/')">Return to Gstocky</button>
      </template>

      <template v-else>
        <div class="connection-identity" aria-label="Connection request">
          <div class="identity-app">
            <connector-brand-icon :brand="connectorBrand" :size="52" />
            <strong>{{ connectorName }}</strong>
          </div>
          <div class="connection-arrow" aria-hidden="true"><span></span><b>›</b></div>
          <div class="identity-app">
            <connector-brand-icon brand="gstocky" :size="52" />
            <strong>Gstocky</strong>
          </div>
        </div>

        <h1 id="oauth-title">Connect {{ connectorName }} to Gstocky</h1>
        <p>
          This will let <strong>{{ connectorName }}</strong> use Gstocky for
          <strong>{{ consent.business.name }}</strong> when you ask it to.
        </p>

        <div class="permissions">
          <h2>Permissions requested</h2>
          <ul>
            <li v-for="scope in consent.scopes" :key="scope">
              <span class="check">✓</span>
              <span>{{ scopeLabels[scope] || scope }}</span>
            </li>
          </ul>
        </div>

        <p class="notice">Invoices and quotes are created as pending. Gstocky will not send documents or record payments automatically.</p>
        <div class="actions">
          <button class="secondary" type="button" :disabled="submitting" @click="decide(false)">Cancel</button>
          <button class="primary" type="button" :disabled="submitting" @click="decide(true)">
            {{ submitting ? 'Connecting…' : 'Allow connection' }}
          </button>
        </div>
      </template>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import ConnectorBrandIcon from '../components/ConnectorBrandIcon.vue'

export default {
  name: 'OAuthAuthorize',
  components: { ConnectorBrandIcon },
  data () {
    return {
      loading: true,
      submitting: false,
      error: '',
      consent: null,
      scopeLabels: {
        profile: 'See your basic Gstocky profile',
        'clients:read': 'Search and view clients',
        'products:read': 'Search products, prices, and stock',
        'invoices:read': 'View invoices and their status',
        'invoices:write': 'Preview and create pending invoices',
        'quotes:write': 'Preview and create pending quotes'
      }
    }
  },
  computed: {
    connectorBrand () {
      if (this.consent?.client_platform === 'claude') return 'claude'
      if (this.consent?.client_platform === 'chatgpt') return 'chatgpt'
      return 'generic'
    },
    connectorName () {
      if (this.connectorBrand === 'claude') return 'Claude'
      if (this.connectorBrand === 'chatgpt') return 'ChatGPT'
      return this.consent?.client_name || 'AI assistant'
    }
  },
  async mounted () {
    document.title = 'Connect to Gstocky'
    try {
      const response = await axios.get(`${process.env.VUE_APP_API}/oauth/consent`, { params: this.protocolParams() })
      this.consent = response.data
    } catch (error) {
      this.error = error.response?.data?.error_description || 'This authorization request is invalid or has expired.'
    } finally {
      this.loading = false
    }
  },
  methods: {
    protocolParams () {
      const allowed = ['client_id', 'redirect_uri', 'response_type', 'scope', 'state', 'code_challenge', 'code_challenge_method', 'resource']
      return allowed.reduce((params, key) => {
        if (typeof this.$route.query[key] === 'string') params[key] = this.$route.query[key]
        return params
      }, {})
    },
    async decide (approved) {
      this.submitting = true
      this.error = ''
      try {
        const response = await axios.post(`${process.env.VUE_APP_API}/oauth/consent`, {
          ...this.protocolParams(),
          approved
        })
        window.location.assign(response.data.redirect_url)
      } catch (error) {
        this.error = error.response?.data?.error_description || 'Gstocky could not complete this connection.'
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.oauth-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: #f4f7fb;
  color: #172033;
}
.oauth-card {
  width: min(100%, 540px);
  padding: 36px;
  border: 1px solid #e1e7f0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(24, 38, 62, 0.12);
}
.brand { display: flex; align-items: center; gap: 10px; color: #174ea6; font-size: 1.1rem; font-weight: 800; margin-bottom: 24px; }
.connection-identity { display: grid; grid-template-columns: 1fr 58px 1fr; align-items: center; margin: 8px 0 28px; padding: 18px; border: 1px solid #e4eaf3; border-radius: 16px; background: linear-gradient(135deg, #fbfdff, #f4f7fc); }
.identity-app { display: grid; justify-items: center; gap: 8px; color: #25324a; font-size: .9rem; }
.connection-arrow { position: relative; display: grid; place-items: center; color: #2667ff; }
.connection-arrow span { position: absolute; width: 38px; height: 2px; background: #b9cdfb; }
.connection-arrow b { position: relative; margin-left: 30px; font-size: 2rem; line-height: 1; }
h1 { margin: 0 0 12px; font-size: 1.8rem; }
h2 { margin: 0 0 14px; font-size: 1rem; }
p { line-height: 1.55; color: #5b6578; }
.permissions { margin: 26px 0; padding: 20px; border-radius: 12px; background: #f7f9fc; }
ul { margin: 0; padding: 0; list-style: none; }
li { display: flex; gap: 10px; margin: 12px 0; line-height: 1.4; }
.check { color: #16865b; font-weight: 800; }
.notice { padding: 12px 14px; border-left: 3px solid #2667ff; background: #f4f7ff; font-size: .9rem; }
.error { color: #b42318; }
.actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 28px; }
button { min-height: 44px; padding: 10px 18px; border-radius: 9px; font-weight: 700; cursor: pointer; }
button:disabled { opacity: .65; cursor: wait; }
.primary { border: 1px solid #2667ff; background: #2667ff; color: #fff; }
.secondary { border: 1px solid #cdd5e2; background: #fff; color: #28344a; }
@media (max-width: 560px) {
  .oauth-card { padding: 24px; }
  .actions { flex-direction: column-reverse; }
  button { width: 100%; }
}
</style>
